"use client";

/**
 * Altura automática que abre e fecha: acordeão, campo condicional, erro
 * embaixo do campo, filtro avançado. O resto da tela se reorganiza junto em
 * vez de pular.
 *
 * E ACOMPANHA: aberto, se o miolo muda de altura (um campo novo, uma lista que
 * cresce, um erro que aparece lá dentro), a caixa anda até a altura nova na
 * mesma curva, em vez de saltar.
 *
 * `overflow: hidden` só ENQUANTO anda: parado, o anel de foco de um campo lá
 * dentro não é cortado. Margem vertical do primeiro e do último filho vaza pra
 * fora da conta de altura e dá um tranco no fim; use padding no miolo.
 */
import { AnimatePresence, m } from "motion/react";
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useMovimento } from "./provider";

export function Recolher({
  aberto,
  children,
  className,
  style,
  animarNaChegada = false,
  acompanhar = true,
  id,
}: {
  aberto: boolean;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Anima também quando já nasce aberto (padrão: aparece pronto). */
  animarNaChegada?: boolean;
  /** Aberto, a caixa acompanha o miolo quando ele muda de altura (padrão: sim). */
  acompanhar?: boolean;
  id?: string;
}) {
  const mov = useMovimento();
  const [andando, setAndando] = useState(false);
  const miolo = useRef<HTMLDivElement | null>(null);
  const [altura, setAltura] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!aberto || !acompanhar) return;
    const el = miolo.current;
    if (!el) return;
    const medir = () => setAltura(el.offsetHeight);
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [aberto, acompanhar]);

  const r = mov.r.recolher;
  /* Depois de medido, o alvo é a altura em px: é o que deixa a caixa andar
     quando o miolo muda com ela aberta. Antes, "auto" (a receita). */
  const alvo =
    acompanhar && altura !== null && typeof r.animate === "object"
      ? {
          ...r.animate,
          height: altura,
          transition: { ...(r.animate.transition as object), height: mov.reduzido ? { duration: 0 } : mov.tempo("md") },
        }
      : r.animate;

  return (
    <AnimatePresence initial={animarNaChegada}>
      {aberto ? (
        <m.div
          key="recolher"
          id={id}
          initial={r.initial}
          animate={alvo}
          exit={r.exit}
          className={className}
          style={{ ...style, overflow: andando ? "hidden" : style?.overflow }}
          onAnimationStart={() => setAndando(true)}
          onAnimationComplete={() => setAndando(false)}
        >
          <div ref={miolo} style={{ display: "flow-root" }}>
            {children}
          </div>
        </m.div>
      ) : null}
    </AnimatePresence>
  );
}
